import { faEllipsisV, faRotateLeft } from '@fortawesome/pro-regular-svg-icons'
import { ActionIcon, Flex, Text, NumberInput, Menu } from '@mantine/core'
import { useState, useEffect } from 'react'

import { useTranslation } from '@flecto/translations'
import { type CoreBookingProduct } from '@flecto/types'
import {
  Badge,
  DataTable,
  type DataTableProps,
  type DataTableColumn,
  InputCurrency,
  TextCurrency,
  Icon,
  AvatarProduct,
} from '@flecto/ui'
import { deepClone } from '@flecto/utils'

type TableBookingProductsReturnProps = Omit<
  DataTableProps<CoreBookingProduct>,
  'columns'
> & {
  bookingProducts?: CoreBookingProduct[]
  onSelectBookingProducts?: (bookingProducts: CoreBookingProduct[]) => void
}

export function TableBookingProductsReturn({
  bookingProducts,
  onSelectBookingProducts,
  ...props
}: TableBookingProductsReturnProps) {
  const { t, i18n } = useTranslation('saas', {
    keyPrefix: 'booking_pickup_return',
  })

  const [selectedRecords, setSelectedRecords] = useState<CoreBookingProduct[]>(
    []
  )
  const [tableBookingProducts, setTableBookingProducts] = useState<
    CoreBookingProduct[]
  >([])

  const handleSelectRows = (selected: CoreBookingProduct[]) => {
    setSelectedRecords(selected)
    onSelectBookingProducts?.(selected)
  }

  const getRowInfo = (row: CoreBookingProduct) => {
    const isSelected = selectedRecords.includes(row)

    const currentReturnedQuantity = !isSelected ? 0 : row.returned_quantity

    const inputMax =
      row.picked_up_quantity - (row.local_old_returned_quantity ?? 0)

    const itemsLeft = !isSelected
      ? (row.returned_quantity ?? 0) - (row.local_old_returned_quantity ?? 0)
      : inputMax - row.returned_quantity

    return { isSelected, currentReturnedQuantity, inputMax, itemsLeft }
  }

  const columns: DataTableColumn<CoreBookingProduct>[] = [
    {
      accessor: 'name',
      title: t('table_return_header_product'),
      width: 200,
      ellipsis: true,
      render: (row) => (
        <Flex gap='sm' align='center'>
          <AvatarProduct product={row.product} />
          <Text truncate>{row.product?.title || ''}</Text>
        </Flex>
      ),
    },
    {
      accessor: 'returned',
      title: t('table_return_header_returned'),
      width: 90,
      render: (row) => (
        <Text c='dimmed'>
          {Number(row.local_old_returned_quantity)} {t('label_of')}{' '}
          {Number(row.picked_up_quantity)}
        </Text>
      ),
    },
    {
      accessor: 'returning',
      title: t('table_return_header_returning'),
      width: 220,
      render: (row, index) => {
        const { isSelected, currentReturnedQuantity, inputMax, itemsLeft } =
          getRowInfo(row)

        return (
          <Flex align='center' gap='sm'>
            <NumberInput
              disabled={inputMax <= 0 || !isSelected}
              size='sm'
              w={120}
              min={0}
              max={inputMax}
              value={currentReturnedQuantity}
              onChange={(value) => {
                row.returned_quantity = Number(value)
                row.local_new_returned_quantity =
                  Number(value) + (row.local_old_returned_quantity ?? 0)

                tableBookingProducts[index] = row

                setTableBookingProducts([...tableBookingProducts])

                const selectedIds = selectedRecords.map((item) => item.id)
                const newSelected = tableBookingProducts.filter((item) =>
                  selectedIds.includes(item.id)
                )

                handleSelectRows(newSelected)
              }}
            />

            <Badge color={itemsLeft <= 0 ? 'gray' : 'orange'}>
              {i18n.language === 'pt' ? (
                <>
                  {itemsLeft <= 1
                    ? t('label_left').slice(0, -1)
                    : t('label_left')}
                  {` ${itemsLeft}`}
                </>
              ) : (
                <>
                  {itemsLeft} {t('label_left')}
                </>
              )}
            </Badge>
          </Flex>
        )
      },
    },
    {
      accessor: 'deposit_cents',
      title: t('table_return_header_deposit'),
      width: 90,
      render: (row) => <TextCurrency cents={Number(row.deposit_cents ?? 0)} />,
    },
    {
      accessor: 'keep_deposit',
      title: t('table_return_header_keep_deposit'),
      width: 130,
      render: (row, index) => {
        return (
          <InputCurrency
            disabled={row.picked_up_quantity <= 0 || row.deposit_cents <= 0}
            size='sm'
            min={0}
            w={120}
            value={Number(row.kept_deposit_cents)}
            max={row.deposit_cents}
            onChange={(cents) => {
              row.kept_deposit_cents = Number(cents)
              tableBookingProducts[index] = row

              setTableBookingProducts([...tableBookingProducts])

              const selectedIds = selectedRecords.map((item) => item.id)
              const newSelected = tableBookingProducts.filter((item) =>
                selectedIds.includes(item.id)
              )

              handleSelectRows(newSelected)
            }}
          />
        )
      },
    },
    {
      accessor: 'actions',
      title: '',
      width: 50,
      textAlign: 'right',
      render: (row, index) => {
        const resetReturns = () => {
          row.returned_quantity = 0
          row.local_old_returned_quantity = 0
          row.local_new_returned_quantity = 0
          row.kept_deposit_cents = 0

          tableBookingProducts[index] = row

          setTableBookingProducts([...tableBookingProducts])

          const selectedIds = selectedRecords.map((item) => item.id)
          const newSelected = tableBookingProducts.filter((item) =>
            selectedIds.includes(item.id)
          )

          handleSelectRows(newSelected)
        }

        return (
          <Menu position='top-end' withinPortal zIndex={9999}>
            <Menu.Target>
              <ActionIcon size='sm'>
                <Icon icon={faEllipsisV} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                disabled={(row.local_old_returned_quantity ?? 0) <= 0}
                leftSection={<Icon icon={faRotateLeft} />}
                onClick={resetReturns}
              >
                {t('label_reset_returns')}
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        )
      },
    },
  ]

  useEffect(() => {
    const items = deepClone(bookingProducts ?? [])
      .map((item: CoreBookingProduct) => {
        item.local_old_returned_quantity = item.returned_quantity
        item.local_new_returned_quantity = item.picked_up_quantity
        item.returned_quantity =
          item.picked_up_quantity - item.returned_quantity

        return item
      })
      .sort((a, b) => (a.returned_quantity < b.returned_quantity ? 1 : -1))

    setTableBookingProducts(items)
    handleSelectRows(items.filter((item) => item.picked_up_quantity > 0))
  }, [bookingProducts])

  return (
    <DataTable
      {...props}
      columns={columns}
      records={tableBookingProducts}
      selectedRecords={selectedRecords}
      onSelectedRecordsChange={handleSelectRows}
      isRecordSelectable={(record) => record.picked_up_quantity > 0}
      getRecordSelectionCheckboxProps={(row) => {
        const inputMax =
          row.picked_up_quantity - (row.local_old_returned_quantity ?? 0)

        return {
          disabled: inputMax <= 0,
        }
      }}
    />
  )
}
